import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Badge,
  Progress,
  Alert,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  WarningOutlined,
  FileExcelOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import dayjs from "dayjs";
import {
  IPriceChangeRequest,
  PriceChangeRequestStatus,
  MoneyPair,
} from "../../types/priceChangeRequest.type";
import { priceChangeRequestApi, RecomputeJob } from "../../api/priceChangeRequestApi";
import { formatNumber } from "../../utils/formatNumber";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const statusMeta: Record<
  PriceChangeRequestStatus,
  { color: string; text: string }
> = {
  [PriceChangeRequestStatus.PENDING]: { color: "orange", text: "Ожидает" },
  [PriceChangeRequestStatus.APPROVED]: { color: "green", text: "Одобрен" },
  [PriceChangeRequestStatus.REJECTED]: { color: "red", text: "Отклонён" },
  [PriceChangeRequestStatus.SUPERSEDED]: { color: "default", text: "Заменён" },
};

// Пара «сейчас / новая» в сумах (UZS): верхняя строка — текущее значение,
// нижняя — новое, с подсветкой и стрелкой направления изменения.
function DeltaCell({
  from,
  to,
  fromLabel = "Сейчас",
  toLabel = "Новая",
}: {
  from: MoneyPair;
  to: MoneyPair;
  fromLabel?: string;
  toLabel?: string;
}) {
  const up = to.UZS > from.UZS;
  const same = Math.abs(to.UZS - from.UZS) < 0.001;
  const color = same ? undefined : up ? "#cf1322" : "#389e0d";
  return (
    <Space direction="vertical" size={2} style={{ lineHeight: 1.3 }}>
      <span style={{ fontSize: 12, color: "#8c8c8c" }}>
        {fromLabel}: {formatNumber(from.UZS)} сум
      </span>
      <span style={{ fontWeight: 600, color }}>
        {toLabel}: {formatNumber(to.UZS)} сум
        {!same &&
          (up ? (
            <ArrowUpOutlined style={{ marginLeft: 4 }} />
          ) : (
            <ArrowDownOutlined style={{ marginLeft: 4 }} />
          ))}
      </span>
    </Space>
  );
}

export default function PriceChangeRequests() {
  const [requests, setRequests] = useState<IPriceChangeRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState<PriceChangeRequestStatus | "all">(
    PriceChangeRequestStatus.PENDING
  );

  const [selected, setSelected] = useState<IPriceChangeRequest | null>(null);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [rejectForm] = Form.useForm();

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [recomputeLoading, setRecomputeLoading] = useState(false);
  // Прогресс фонового пересчёта (null — не идёт).
  const [recomputeJob, setRecomputeJob] = useState<RecomputeJob | null>(null);
  const recomputePollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { loading: exportLoading, downloadFile } = useDownloadFile();

  // Выбор строк, поиск, сортировка, массовые операции
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [isBulkRejectOpen, setIsBulkRejectOpen] = useState(false);
  const [bulkRejectForm] = Form.useForm();

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await priceChangeRequestApi.getRequests({
        page,
        limit: pageSize,
        status,
        search: search || undefined,
        sortBy,
        sortOrder,
      });
      setRequests(res.data || []);
      setTotal(res.total || 0);
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при загрузке запросов");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    setSelectedRowKeys([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, status, search, sortBy, sortOrder]);

  const handleApprove = async (record: IPriceChangeRequest) => {
    setActionLoading(record._id);
    try {
      await priceChangeRequestApi.approve(record._id);
      message.success("Цены применены");
      // Обработанный запрос убираем из выделения: иначе он попал бы в
      // следующую массовую операцию и вернулся бы как «уже обработан».
      setSelectedRowKeys((keys) => keys.filter((k) => String(k) !== record._id));
      fetchRequests();
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при одобрении");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (values: any) => {
    if (!selected) return;
    setRejectLoading(true);
    try {
      await priceChangeRequestApi.reject(selected._id, { reason: values.reason });
      message.success("Запрос отклонён");
      setIsRejectOpen(false);
      rejectForm.resetFields();
      setSelectedRowKeys((keys) => keys.filter((k) => String(k) !== selected._id));
      fetchRequests();
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при отклонении");
    } finally {
      setRejectLoading(false);
    }
  };

  /**
   * Показывает итог массовой операции. Если часть позиций не прошла — говорим
   * об этом явно и оставляем их выделенными, чтобы можно было повторить только
   * их. Раньше любой результат показывался как success и неуспешные позиции
   * молча терялись из выделения.
   */
  const reportBulkResult = (
    res: { message: string; failed: number; failures?: Array<{ id: string; error: string }> },
    okKeys: React.Key[]
  ) => {
    if (res.failed > 0) {
      const failures = res.failures || [];
      const failedIds = new Set(failures.map((f) => f.id));
      const reasons = failures
        .map((f) => f.error)
        .filter((err, i, arr) => arr.indexOf(err) === i)
        .slice(0, 3);

      message.warning({
        content: `${res.message}${reasons.length ? ` Причины: ${reasons.join("; ")}` : ""}`,
        duration: 8,
      });
      // Оставляем в выделении только непрошедшие — для повтора.
      setSelectedRowKeys(okKeys.filter((k) => failedIds.has(String(k))));
    } else {
      message.success(res.message);
      setSelectedRowKeys([]);
    }
    fetchRequests();
  };

  const handleBulkApprove = async () => {
    setBulkLoading(true);
    const ids = selectedRowKeys as React.Key[];
    try {
      const res = await priceChangeRequestApi.bulkApprove({ ids: ids as string[] });
      reportBulkResult(res, ids);
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при массовом одобрении");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkReject = async (values: any) => {
    setBulkLoading(true);
    const ids = selectedRowKeys as React.Key[];
    try {
      const res = await priceChangeRequestApi.bulkReject({
        ids: ids as string[],
        reason: values.reason,
      });
      setIsBulkRejectOpen(false);
      bulkRejectForm.resetFields();
      reportBulkResult(res, ids);
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при массовом отклонении");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleExport = () => {
    downloadFile("/price-change-request/export", "price_change_requests.xlsx", { status });
  };

  const stopRecomputePolling = () => {
    if (recomputePollRef.current) {
      clearInterval(recomputePollRef.current);
      recomputePollRef.current = null;
    }
  };

  /**
   * Опрашивает статус фонового пересчёта. Пересчёт больше не выполняется внутри
   * HTTP-запроса, поэтому таймаут прокси его не рвёт, а прогресс виден сразу.
   */
  const pollRecompute = (jobId: string) => {
    stopRecomputePolling();

    recomputePollRef.current = setInterval(async () => {
      try {
        const job = await priceChangeRequestApi.getRecomputeStatus(jobId);
        setRecomputeJob(job);

        if (job.status !== "running") {
          stopRecomputePolling();
          setRecomputeLoading(false);

          if (job.status === "done") {
            message.success(
              `Пересчёт завершён. Создано запросов: ${job.created}, пропущено: ${job.skipped}.`
            );
            setStatus(PriceChangeRequestStatus.PENDING);
            fetchRequests();
          } else {
            message.error(`Пересчёт завершился с ошибкой: ${job.error || "неизвестно"}`);
          }
        }
      } catch (error: any) {
        // Задача не найдена (напр. сервер перезапустился) — прекращаем опрос,
        // чтобы не крутить бесконечно.
        if (error?.response?.status === 404) {
          stopRecomputePolling();
          setRecomputeLoading(false);
          setRecomputeJob(null);
          message.warning("Задача пересчёта не найдена. Обновите страницу.");
        }
      }
    }, 1500);
  };

  const handleRecompute = async () => {
    setRecomputeLoading(true);
    try {
      const res = await priceChangeRequestApi.recompute();
      message.info(res.message || "Пересчёт запущен.");
      setRecomputeJob({
        id: res.jobId,
        status: "running",
        total: res.total,
        processed: 0,
        created: 0,
        skipped: 0,
        startedAt: new Date().toISOString(),
      });
      pollRecompute(res.jobId);
    } catch (error: any) {
      setRecomputeLoading(false);
      // 409 — пересчёт уже идёт: подхватываем существующую задачу вместо ошибки.
      const active: RecomputeJob | undefined = error?.response?.data?.job;
      if (error?.response?.status === 409 && active) {
        message.warning("Пересчёт уже выполняется — показываю его прогресс.");
        setRecomputeJob(active);
        setRecomputeLoading(true);
        pollRecompute(active.id);
        return;
      }
      message.error(error.response?.data?.error || "Ошибка при пересчёте");
    }
  };

  // Если пересчёт уже идёт (запущен в другой вкладке или до перезагрузки
  // страницы) — подхватываем его прогресс при открытии раздела.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const job = await priceChangeRequestApi.getRecomputeStatus();
        if (!cancelled && job?.status === "running") {
          setRecomputeJob(job);
          setRecomputeLoading(true);
          pollRecompute(job.id);
        }
      } catch {
        // 404 — активного пересчёта нет, это нормальное состояние.
      }
    })();

    return () => {
      cancelled = true;
      stopRecomputePolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      title: "Продукция",
      key: "produceName",
      sorter: true,
      render: (record: IPriceChangeRequest) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.produceName}</Text>
          {record.categoryName && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.categoryName}
            </Text>
          )}
          {record.costIncomplete && (
            <Tooltip
              title={`Нет цены у материалов: ${(record.missingMaterials || []).join(", ")}`}
            >
              <Tag icon={<WarningOutlined />} color="warning" style={{ marginTop: 4 }}>
                Себестоимость неполная
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Причина",
      dataIndex: "reason",
      key: "reason",
      render: (reason: string) => (
        <Text style={{ fontSize: 12 }}>{reason}</Text>
      ),
    },
    {
      title: "Себестоимость (расчёт)",
      key: "newCost.UZS",
      sorter: true,
      render: (record: IPriceChangeRequest) => (
        <DeltaCell
          from={record.oldCost}
          to={record.newCost}
          fromLabel="Было"
          toLabel="Стало"
        />
      ),
    },
    {
      title: "Мин. цена (пол продажи)",
      key: "proposedLowTradePrice.UZS",
      sorter: true,
      render: (record: IPriceChangeRequest) =>
        record.lowTradePriceChanged ? (
          <Space direction="vertical" size={2}>
            <DeltaCell from={record.currentLowTradePrice} to={record.proposedLowTradePrice} />
            {record.minProfitPercent != null && (
              <Tag color="cyan" style={{ marginInlineEnd: 0 }}>
                наценка +{record.minProfitPercent}% к себест.
              </Tag>
            )}
          </Space>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatNumber(record.currentLowTradePrice.UZS)} сум · без изменений
          </Text>
        ),
    },
    {
      // Прайс умный прайс не меняет — показываем как справку.
      title: "Прайс (справочно)",
      key: "currentPrice.UZS",
      sorter: true,
      render: (record: IPriceChangeRequest) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatNumber(record.currentPrice.UZS)} сум
        </Text>
      ),
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      render: (s: PriceChangeRequestStatus) => (
        <Badge status={statusMeta[s].color as any} text={statusMeta[s].text} />
      ),
    },
    {
      title: "Создан",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      defaultSortOrder: "descend" as const,
      render: (date: string) => (
        <Text style={{ fontSize: 12 }}>{dayjs(date).format("DD.MM.YYYY HH:mm")}</Text>
      ),
    },
    {
      title: "Действия",
      key: "actions",
      render: (record: IPriceChangeRequest) =>
        record.status === PriceChangeRequestStatus.PENDING ? (
          <Space>
            <Popconfirm
              title="Применить новую мин. цену?"
              description="Мин. цена продукции будет обновлена на предложенную. Прайс не меняется."
              okText="Применить"
              cancelText="Отмена"
              onConfirm={() => handleApprove(record)}
            >
              <Tooltip title="Одобрить">
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  style={{ color: "green" }}
                  loading={actionLoading === record._id}
                />
              </Tooltip>
            </Popconfirm>
            <Tooltip title="Отклонить">
              <Button
                type="text"
                icon={<CloseOutlined />}
                danger
                onClick={() => {
                  setSelected(record);
                  setIsRejectOpen(true);
                }}
              />
            </Tooltip>
          </Space>
        ) : record.status === PriceChangeRequestStatus.REJECTED &&
          record.rejectionReason ? (
          <Tooltip title={record.rejectionReason}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              причина
            </Text>
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              Запросы на изменение цен
            </Title>
            <Text type="secondary">
              Формируются автоматически при изменении себестоимости продукции. В колонках:{" "}
              <b>Сейчас</b> — действующая цена, <b>Новая</b> — предлагаемая (
              <span style={{ color: "#cf1322" }}>▲ выше</span> /{" "}
              <span style={{ color: "#389e0d" }}>▼ ниже</span>).
            </Text>
          </Col>
          <Col>
            <Space>
              <Select
                style={{ width: 180 }}
                value={status}
                onChange={(v) => {
                  setStatus(v);
                  setPage(1);
                }}
              >
                <Option value={PriceChangeRequestStatus.PENDING}>Ожидают</Option>
                <Option value={PriceChangeRequestStatus.APPROVED}>Одобрены</Option>
                <Option value={PriceChangeRequestStatus.REJECTED}>Отклонены</Option>
                <Option value={PriceChangeRequestStatus.SUPERSEDED}>Заменены</Option>
                <Option value="all">Все</Option>
              </Select>
              <Button
                icon={<FileExcelOutlined />}
                loading={exportLoading}
                onClick={handleExport}
              >
                Экспорт в Excel
              </Button>
              <Popconfirm
                title="Пересчитать умный прайс?"
                description="Сформирует запросы по всей продукции с заданными % прибыли."
                okText="Пересчитать"
                cancelText="Отмена"
                onConfirm={handleRecompute}
              >
                <Button
                  icon={<ReloadOutlined />}
                  loading={recomputeLoading}
                  disabled={recomputeJob?.status === "running"}
                >
                  Пересчитать всё
                </Button>
              </Popconfirm>
            </Space>
          </Col>
        </Row>

        {recomputeJob && recomputeJob.status === "running" && (
          <Alert
            style={{ marginBottom: 12 }}
            type="info"
            message={
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text>
                  Пересчёт умного прайса: {recomputeJob.processed} из {recomputeJob.total}
                  {" · "}создано: {recomputeJob.created}
                  {" · "}пропущено: {recomputeJob.skipped}
                </Text>
                <Progress
                  percent={
                    recomputeJob.total > 0
                      ? Math.round((recomputeJob.processed / recomputeJob.total) * 100)
                      : 0
                  }
                  status="active"
                  size="small"
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Можно продолжать работу — пересчёт идёт на сервере.
                </Text>
              </Space>
            }
          />
        )}

        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }} gutter={[8, 8]}>
          <Col>
            <Input.Search
              placeholder="Поиск: продукт, категория, причина"
              allowClear
              style={{ width: 340 }}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onSearch={(v) => {
                setSearch(v.trim());
                setPage(1);
              }}
            />
          </Col>
          <Col>
            {selectedRowKeys.length > 0 && status === PriceChangeRequestStatus.PENDING && (
              <Space>
                <Text type="secondary">Выбрано: {selectedRowKeys.length}</Text>
                <Popconfirm
                  title={`Одобрить выбранные (${selectedRowKeys.length})?`}
                  description="У этих продуктов мин. цена будет обновлена на предложенную. Прайс не меняется."
                  okText="Одобрить"
                  cancelText="Отмена"
                  onConfirm={handleBulkApprove}
                >
                  <Button type="primary" icon={<CheckOutlined />} loading={bulkLoading}>
                    Одобрить выбранные
                  </Button>
                </Popconfirm>
                <Button
                  danger
                  icon={<CloseOutlined />}
                  loading={bulkLoading}
                  onClick={() => setIsBulkRejectOpen(true)}
                >
                  Отклонить выбранные
                </Button>
              </Space>
            )}
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={requests}
          loading={loading}
          rowKey="_id"
          scroll={{ x: "max-content" }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
            getCheckboxProps: (record: IPriceChangeRequest) => ({
              disabled: record.status !== PriceChangeRequestStatus.PENDING,
            }),
          }}
          onChange={(pag: any, _filters: any, sorter: any) => {
            const s = Array.isArray(sorter) ? sorter[0] : sorter;
            const nextSortBy = s && s.order ? String(s.columnKey) : "createdAt";
            const nextOrder: "asc" | "desc" =
              s && s.order ? (s.order === "ascend" ? "asc" : "desc") : "desc";
            const sortChanged = nextSortBy !== sortBy || nextOrder !== sortOrder;
            setSortBy(nextSortBy);
            setSortOrder(nextOrder);
            setPage(sortChanged ? 1 : pag.current || 1);
            setPageSize(pag.pageSize || pageSize);
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `Всего: ${t}`,
          }}
        />
      </Card>

      <Modal
        title="Отклонить запрос"
        open={isRejectOpen}
        onCancel={() => {
          setIsRejectOpen(false);
          rejectForm.resetFields();
        }}
        footer={null}
      >
        <Form form={rejectForm} onFinish={handleReject} layout="vertical">
          <Form.Item name="reason" label="Причина отклонения (необязательно)">
            <TextArea rows={3} placeholder="Например: подождать стабилизации цен" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button danger htmlType="submit" icon={<CloseOutlined />} loading={rejectLoading}>
                Отклонить
              </Button>
              <Button
                onClick={() => {
                  setIsRejectOpen(false);
                  rejectForm.resetFields();
                }}
              >
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Отклонить выбранные (${selectedRowKeys.length})`}
        open={isBulkRejectOpen}
        onCancel={() => {
          setIsBulkRejectOpen(false);
          bulkRejectForm.resetFields();
        }}
        footer={null}
      >
        <Form form={bulkRejectForm} onFinish={handleBulkReject} layout="vertical">
          <Form.Item name="reason" label="Причина отклонения (необязательно)">
            <TextArea rows={3} placeholder="Общая причина для всех выбранных" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button danger htmlType="submit" icon={<CloseOutlined />} loading={bulkLoading}>
                Отклонить {selectedRowKeys.length}
              </Button>
              <Button
                onClick={() => {
                  setIsBulkRejectOpen(false);
                  bulkRejectForm.resetFields();
                }}
              >
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
